import React from 'react';
import ReplyBox from 'routes/conversations/components/ReplyBox';
import {
  EditorState,
  ContentState,
  convertFromRaw,
  convertToRaw,
} from 'draft-js';
import PropTypes from 'prop-types';
import { CONSTS } from 'constants/index.js';
import { isValueEditorObject } from 'utilities/editorObject';
import './index.scss';

const getNewEditorStateContent = ({ value, formatAsEditorObject }) => {
  const isEditorObject = formatAsEditorObject && isValueEditorObject(value);
  if (isEditorObject) {
    return convertFromRaw(value);
  }

  return ContentState.createFromText(value);
};

const getNewEditorState = ({ value, formatAsEditorObject }) => {
  if (value) {
    const newEditorStateContent = getNewEditorStateContent({
      value,
      formatAsEditorObject,
    });
    return EditorState.createWithContent(newEditorStateContent);
  }

  return EditorState.createEmpty();
};

const useEditorState = (value, onChange, formatAsEditorObject) => {
  const [internalEditorState, setInternalEditorState] = React.useState(
    getNewEditorState({ value, formatAsEditorObject })
  );

  React.useEffect(() => {
    const newEditorState = getNewEditorState({ value, formatAsEditorObject });
    const editorStateWithEndFocus = EditorState.moveFocusToEnd(newEditorState);

    setInternalEditorState(editorStateWithEndFocus);
  }, [Boolean(value)]);

  const getValueFromEditorState = (editorState) => {
    if (formatAsEditorObject) {
      return convertToRaw(editorState.getCurrentContent());
    }
    return editorState.getCurrentContent().getPlainText();
  };

  const setEditorState = (newEditorState) => {
    setInternalEditorState(newEditorState);
    const newValue = getValueFromEditorState(newEditorState);
    onChange(newValue);
  };

  return [internalEditorState, setEditorState];
};

const ReplyBoxField = (props) => {
  const {
    value,
    onChange,
    formatAsEditorObject,
    editorMode,
    toolbarClassName,
    compileTemplateTags,
    templateContext,
    meta,
    placeholder,
    autoFocus,
  } = props;

  const [editorState, setEditorState] = useEditorState(
    value,
    onChange,
    formatAsEditorObject
  );

  const updateCallbacks = {
    editorState: setEditorState,
  };

  return (
    <div className="ReplyBoxField">
      <ReplyBox
        editorMode={editorMode}
        toolbarClassName={toolbarClassName}
        updateCallbacks={updateCallbacks}
        compileTemplateTags={compileTemplateTags}
        templateContext={templateContext}
        editorState={editorState}
        meta={meta}
        placeholder={placeholder}
        autoFocus={autoFocus}
      />
    </div>
  );
};

ReplyBoxField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  formatAsEditorObject: PropTypes.bool.isRequired,
  editorMode: PropTypes.string,
  toolbarClassName: PropTypes.string,
  compileTemplateTags: PropTypes.bool,
  templateContext: PropTypes.arrayOf(PropTypes.shape()),
  meta: PropTypes.shape(),
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
};

ReplyBoxField.defaultProps = {
  value: '',
  onChange: () => {},
  updateCallbacks: [],
  editorMode: CONSTS.EDITOR_MODE_TYPE.PLAIN,
  toolbarClassName: 'compose-toolbar',
  compileTemplateTags: false,
  templateContext: [],
  meta: {},
  placeholder: '',
  autoFocus: false,
};

export default ReplyBoxField;
